import React, { useEffect, useRef, useState } from 'react';
import './GanttChart.css';
import HorizontalLegendComponent from "../HorizontalLegendComponent/HorizontalLegendComponent";
import LoaderComponent from "../LoaderComponent/LoaderComponent";
import { KeyboardArrowRight as ArrowRight, KeyboardArrowLeft as ArrowLeft } from "@mui/icons-material";
import { Box } from "@mui/material";
import TodayLine from '../TodayLine/TodayLine';


interface Task {
  id: number;
  name: string;
  segments: SegmentColumn[];
  highlight?: boolean;
}

interface Day {
  day: number;
  month: string;
}

interface SegmentColumn {
  index: number;
  type: string;
}

export interface Stage {
  id: number;
  name: string;
  updated_start_date: string | null;
  updated_end_date: string | null;
  original_start_date: string | null;
  original_end_date: string | null;
  finished: number;
  in_progress: number;
  late: number;
  over_year_late: number;
  not_started: number;
  id_number: number;
  has_children: boolean;
}

export interface GanttChartProps {
  stages: Stage[];
  onStageClick?: (data: { id: number; name: string }) => void;
  onStageGoTo?: 'stages' | 'sections' | 'subSections';
  hideDelays?: boolean;
  loading?: boolean;
  backData?: { id: number; name: string }
}

const GanttChart: React.FC<GanttChartProps> = ({
  stages,
  onStageClick,
  onStageGoTo = 'stages',
  hideDelays = false,
  loading = false,
  backData = null
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null!);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [days, setDays] = useState<Day[]>([]);
  const [activeRowIndex, setActiveRowIndex] = useState<number>(-1);

  useEffect(() => {
    const validStages = stages.filter(stage =>
      (stage.updated_start_date || stage.original_start_date) &&
      (stage.updated_end_date || stage.original_end_date)
    );

    const startDates = validStages.map(s =>
      new Date(s.updated_start_date || s.original_start_date!)
    );
    const endDates = validStages.map(s =>
      new Date(s.updated_end_date || s.original_end_date!)
    );

    if (startDates.length === 0 || endDates.length === 0) {
      setTasks([]);
      setDays([]);
      return;
    }

    const realMinDate = new Date(Math.min(...startDates.map(d => d.getTime())));
    const adjustedMinDate = new Date(realMinDate.getFullYear(), 0, 1);
    const maxDate = new Date(Math.max(...endDates.map(d => d.getTime())));
    const adjustedMaxDate = new Date(
      maxDate.getMonth() >= 6 ? maxDate.getFullYear() + 1 : maxDate.getFullYear(),
      6,
      1
    );

    const minYear = adjustedMinDate.getFullYear();
    const maxYear = adjustedMaxDate.getFullYear();
    const monthNames = [
      "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
      "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"
    ];

    const generatedDays: Day[] = [];
    for (let year = minYear; year <= maxYear; year++) {
      for (let month = 0; month < 12; month++) {
        generatedDays.push({
          day: 1,
          month: `${monthNames[month]} ${year.toString().slice(2)}`
        });
      }
    }
    setDays(generatedDays);

    const segmentsFromStage = (stage: Stage, allDays: Day[]): SegmentColumn[] => {
      const startDateStr = stage.updated_start_date || stage.original_start_date;
      const endDateStr = stage.updated_end_date || stage.original_end_date;

      if (!startDateStr || !endDateStr) return [];

      const stageStart = new Date(startDateStr);
      const stageEnd = new Date(endDateStr);

      const fullRange: number[] = [];

      allDays.forEach((day, index) => {
        const [monthName, shortYear] = day.month.split(" ");
        const monthIndex = monthNames.indexOf(monthName);
        const year = 2000 + parseInt(shortYear, 10);
        const currentDate = new Date(year, monthIndex, 1);

        if (currentDate >= stageStart && currentDate <= stageEnd) {
          fullRange.push(index);
        }
      });

      const total = stage.finished + stage.in_progress + stage.late + stage.over_year_late + stage.not_started;
      if (total === 0 || fullRange.length === 0) {
        return fullRange.map(index => ({ index, type: 'gray' }));
      }

      const statusParts = [
        { type: 'green', count: stage.finished },
        { type: 'blue', count: stage.in_progress },
        { type: hideDelays ? 'gray' : 'orange', count: stage.late },
        { type: hideDelays ? 'gray' : 'red', count: stage.over_year_late },
        { type: 'gray', count: stage.not_started },
      ].filter(s => s.count > 0);

      const segments: SegmentColumn[] = [];
      let currentOffset = 0;

      statusParts.forEach(part => {
        const countRatio = part.count / total;
        const rangeCount = Math.round(countRatio * fullRange.length);
        const segmentRange = fullRange.slice(currentOffset, currentOffset + rangeCount);

        segmentRange.forEach(index => segments.push({ index, type: part.type }));
        currentOffset += rangeCount;
      });

      while (currentOffset < fullRange.length) {
        segments.push({ index: fullRange[currentOffset], type: 'gray' });
        currentOffset++;
      }

      return segments;
    };


    const generatedTasks: Task[] = stages.map(stage => ({
      id: stage.id_number,
      name: stage.name,
      segments: segmentsFromStage(stage, generatedDays),
    }));

    setTasks(generatedTasks);
  }, [stages, hideDelays]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!scrollContainerRef.current || days.length === 0) return;

      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear() % 100;
      const monthNames = [
        "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
        "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"
      ];
      const currentMonthLabel = `${monthNames[currentMonth]} ${currentYear}`;

      const targetIndex = days.findIndex(day => day.month === currentMonthLabel);
      if (targetIndex !== -1 && scrollContainerRef.current) {
        const firstCell = scrollContainerRef.current.querySelector('.day-cell') as HTMLElement;
        const cellWidth = firstCell?.offsetWidth || 20;
        const scrollPosition = (targetIndex * cellWidth);
        scrollContainerRef.current.scrollTo({ left: scrollPosition });
      }
    }, );

    return () => clearTimeout(timeout);
  }, [days]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [tasks]);

  const onRowClick = (data: any) => {
    if (activeRowIndex === data.id) {
      onStageClick?.(data);
    } else {
      setActiveRowIndex(data.id);
    }
  };

  return (
    <div aria-label={'תצוגת גרף'} className="gantt-container" dir="rtl" tabIndex={-1}>
      <div aria-hidden="true" className="gantt-content">
        <HorizontalLegendComponent />

        <div className="gantt-chart">
          {loading ? <LoaderComponent fullScreen={false} /> :
            <div>
              <div className="names-column"></div>
              <div className="gantt-scroll-container" ref={scrollContainerRef}>
                <div className="gantt-grid" ref={gridRef}>
                  <TodayLine days={days} gridRef={gridRef} />

                  <div className="gantt-header">
                    <div className="task-header">
                      {backData &&
                        <Box onClick={() => onStageClick?.(backData)} sx={{ color: '#1293FE', cursor: 'pointer', fontWeight: 700, gap: 2, display: 'flex', justifyContent: 'center' }}>
                          <ArrowLeft />
                          {backData.name}
                        </Box>
                      }
                    </div>
                    {days.map((day, index) => {
                      const prevMonth = index > 0 ? days[index - 1].month : null;
                      const isNewMonth = day.month !== prevMonth;

                      if (!day.month.includes("ינואר") && !day.month.includes("יוני")) {
                        return (
                          <div
                            key={index}
                            className={`day-header ${isNewMonth ? 'month-separator' : ''}`}
                          />
                        );
                      }

                      return (
                        <div
                          key={index}
                          className={`day-header ${isNewMonth ? 'month-separator' : ''}`}
                        >
                          <div className="day-text">
                            <span className="month-name">{day.month}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {tasks.map((task, index) => {
                    const stage = stages.find(s => s.id_number === task.id);
                    const isClickable = stage?.has_children;

                    return (
                      <div
                        key={task.id}
                        className={`task-row ${task.id === activeRowIndex ? 'highlighted' : ''}`}
                      >
                        <div
                          className={task.id === activeRowIndex ? "task-name active" : "task-name"}
                          style={{ top: `${index * 50}px` }}
                          onClick={() => {
                            if (isClickable) {
                              onRowClick({ id: task.id, name: task.name });
                            }
                          }}
                        >
                          {task.name}
                          {/*{task.id === activeRowIndex && isClickable && <ArrowRight />}*/}
                          {/*Show arrow only for has chlidrens objects*/}
                          {isClickable && <ArrowRight style={{ marginRight: 4 }} />}
                        </div>

                        <div
                          className={task.id === activeRowIndex ? "task-timeline active" : "task-timeline"}
                          onClick={() => {
                            if (isClickable) {
                              onRowClick({ id: task.id, name: task.name });
                            }
                          }}
                        >
                          {Array.from({ length: days.length }).map((_, dayIndex) => (
                            <div key={dayIndex} className="day-cell"></div>
                          ))}

                          {task.segments.map((segment, segIndex) => {
                            const leftPercent = (segment.index / days.length) * 100 - 1.1;
                            const widthPercent = (1 / days.length) * 100;

                            let segmentClass = "task-segment";
                            if (segment.type === "green") segmentClass += " green";
                            if (segment.type === "blue") segmentClass += " blue";
                            if (segment.type === "gray") segmentClass += " gray";
                            if (segment.type === "red") segmentClass += " red";
                            if (segment.type === "orange") segmentClass += " orange";

                            return (
                              <div
                                key={segIndex}
                                className={segmentClass}
                                style={{
                                  left: `${leftPercent}%`,
                                  width: `${widthPercent}%`,
                                }}
                              ></div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  );
};

export default GanttChart;
